import React, { useEffect } from 'react';

import { PageLayout } from '@/components/templates/PageLayout';
import { CmScrollBox } from '@/components/uiParts';
import { isAvailableFeature } from '@/lib/AccessControl';
import { MenuManagementNewButtonArea } from '@/pages/menu-managements/components/TopButtonArea';
import { MenuManagementItemsForm } from '@/pages/menu-managements/items/components/Form';
import { useMenuManagementItemForm } from '@/pages/menu-managements/items/hooks';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';

const MenuManagementItemsNewPage: React.FC = (): JSX.Element => {
  const {
    associateBrandSelectInput,
    titleJa,
    image,
    memo,
    description,
    salesStartAt,
    salesEndAt,
    error,
    takeout,
    delivery,
    eatIn,
    searchItems,
    selectedItems,
    searchItemName,
    isSearching,
    targetsHeight,
    canServeAlone,
    orderMethods,
    limitOfPurchase,
    handleChangeLimitOfPurchase,
    handleCreate,
    handleInputName,
    handleChangeImageUrl,
    handleClearImage,
    handleChangeCanServeAlone,
    handleChangeOrderMethod,
    handleInputMemo,
    handleInputDescription,
    handleInputSalesStartAt,
    handleInputSalesEndAt,
    handleInputTakeoutPrice,
    handleInputTakeoutPosCode,
    handleInputTakeoutPosCodeCaseOptionGroupItem,
    handleInputTakeoutTax,
    handleInputDeliveryPrice,
    handleInputDeliveryPosCode,
    handleInputDeliveryPosCodeCaseOptionGroupItem,
    handleInputDeliveryTax,
    handleInputEatInPrice,
    handleInputEatInPosCode,
    handleInputEatInPosCodeCaseOptionGroupItem,
    handleInputEatInTax,
    handleOnDragEnd,
    handleSearchItemNameInput,
    handleAddSelectItems,
    handleDeleteDragItem,
    initializeNewPageState,
    isAuthRequired,
    handleChangeIsAuthRequired,
    salesPeriodType,
    handleSalesPeriodType,
    handleSalesRelativeStartAt,
    handleSalesRelativeEndAt,
    salesRelativeStartAt,
    salesRelativeEndAt,
    specialInstructionsEnabled,
    specialInstructionsTitle,
    specialInstructionsPlaceholder,
    specialInstructionsRequired,
    handleChangeSpecialInstructionsEnabled,
    handleInputSpecialInstructionsTitle,
    handleInputSpecialInstructionsPlaceholder,
    handleChangeSpecialInstructionsRequired,
  } = useMenuManagementItemForm();

  const isDisabledSubmitButton = (() => {
    const hasTakeout = orderMethods.includes('TAKE_OUT');
    const hasDelivery = orderMethods.includes('DELIVERY');
    const hasEatIn = orderMethods.includes('EAT_IN');
    return [
      !error.titleJa.isTouched || error.titleJa.isError,
      orderMethods.length === 0,
      hasTakeout &&
        (!error.takeoutPrice.isTouched || error.takeoutPrice.isError),
      hasDelivery &&
        (!error.deliveryPrice.isTouched || error.deliveryPrice.isError),
      hasEatIn && (!error.eatInPrice.isTouched || error.eatInPrice.isError),
      error.memo.isError,
      error.description.isError,
      error.salesPeriod.isError,
      // 商品に関する要望欄が有効でタイトルが空の場合はエラー
      specialInstructionsEnabled && specialInstructionsTitle === '',
      error.specialInstructionsTitle?.isError,
      error.specialInstructionsPlaceholder?.isError,
    ].some((isError) => isError);
  })();

  useEffect(() => {
    (async () => {
      await initializeNewPageState();
    })();
  }, []);

  // 店内飲食のアップセルのため利用可否を判定
  const { managerAccountMe } = useManagerAccountMe();
  const isAvailableEatin = isAvailableFeature(
    'camel_order_eatin',
    managerAccountMe
  );

  return (
    <PageLayout
      title={routes.menuManagementItemsNew.title}
      {...{ associateBrandSelectInput }}
    >
      <MenuManagementNewButtonArea
        listUrl="/menu-managements/items"
        {...{ handleCreate, isDisabledSubmitButton }}
      />
      <CmScrollBox height={`calc(100vh - ${targetsHeight}px)`}>
        <MenuManagementItemsForm
          brandSelectValue={associateBrandSelectInput.value?.value ?? ''}
          page="new"
          {...{
            titleJa,
            image,
            memo,
            description,
            salesStartAt,
            salesEndAt,
            isSearching,
            searchItems,
            selectedItems,
            searchItemName,
            error,
            takeout,
            delivery,
            eatIn,
            canServeAlone,
            orderMethods,
            isAvailableEatin,
            handleInputName,
            handleChangeImageUrl,
            handleClearImage,
            handleInputMemo,
            handleInputDescription,
            handleChangeLimitOfPurchase,
            limitOfPurchase,
            handleInputSalesStartAt,
            handleInputSalesEndAt,
            handleInputTakeoutPrice,
            handleInputTakeoutPosCode,
            handleInputTakeoutPosCodeCaseOptionGroupItem,
            handleInputTakeoutTax,
            handleInputDeliveryPrice,
            handleInputDeliveryPosCode,
            handleInputDeliveryPosCodeCaseOptionGroupItem,
            handleInputDeliveryTax,
            handleInputEatInPrice,
            handleInputEatInPosCode,
            handleInputEatInPosCodeCaseOptionGroupItem,
            handleInputEatInTax,
            handleOnDragEnd,
            handleSearchItemNameInput,
            handleAddSelectItems,
            handleDeleteDragItem,
            handleChangeCanServeAlone,
            handleChangeOrderMethod,
            isAuthRequired,
            handleChangeIsAuthRequired,
            salesPeriodType,
            handleSalesPeriodType,
            handleSalesRelativeStartAt,
            handleSalesRelativeEndAt,
            salesRelativeStartAt,
            salesRelativeEndAt,
            specialInstructionsEnabled,
            specialInstructionsTitle,
            specialInstructionsPlaceholder,
            specialInstructionsRequired,
            handleChangeSpecialInstructionsEnabled,
            handleInputSpecialInstructionsTitle,
            handleInputSpecialInstructionsPlaceholder,
            handleChangeSpecialInstructionsRequired,
          }}
        />
      </CmScrollBox>
    </PageLayout>
  );
};

export default MenuManagementItemsNewPage;
